import { type FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const logoWhiteUrl = "/logo-dark.png";
const logoDarkUrl = "/logo-dark.png";

export function ForgotPasswordPage() {
  const { sendPasswordReset } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const email = emailRef.current?.value.trim() ?? "";

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    setSubmitting(true);
    const { error: resetError } = await sendPasswordReset(email);
    setSubmitting(false);

    if (resetError) {
      setError(resetError);
      return;
    }

    setSuccess(true);
  };

  return (
    <div className="relative isolate flex min-h-screen flex-col">
      <main className="flex flex-1 flex-col">
        <section className="bg-transparent text-[#1f0a18]">
          <div className="grid min-h-screen grid-cols-[1fr_1fr] grid-rows-[auto] auto-cols-fr max-[991px]:grid-cols-[1fr]">
            <div className="relative flex flex-col px-[var(--site-margin)]">
              <div className="mt-5 max-[991px]:absolute max-[991px]:inset-x-[var(--site-margin)] max-[991px]:top-0 max-[991px]:z-[900]">
                <Link to="/" className="inline-block max-w-full pl-0" aria-label="BollyRhythm">
                  <img loading="lazy" src={logoWhiteUrl} alt="BollyRhythm" className="hidden max-w-full align-middle max-[991px]:block" style={{ width: "10rem" }} />
                  <img loading="lazy" src={logoDarkUrl} alt="BollyRhythm" className="inline-block max-w-full align-middle max-[991px]:!hidden" style={{ width: "10rem" }} />
                </Link>
              </div>

              <div className="flex flex-1 flex-col items-center justify-center">
                <div className="mb-5 flex flex-col items-center justify-start gap-1 text-center">
                  <h1 className="m-0 font-display text-[clamp(1.25rem,1.0357rem+1.0714vw,2rem)] font-semibold leading-[1.2]">Forgot Password</h1>
                  <div className="text-neutral text-base">Enter your email and we'll send you a reset link.</div>
                </div>

                <div className="mb-[15px] w-full max-w-[24rem]">
                  {success ? (
                    <div className="rounded-[var(--radius-main)] bg-[#cef5ca] p-2.5 text-center text-sm text-[#114e0b]" role="status">
                      Check your email for password reset instructions!
                    </div>
                  ) : (
                    <form className="m-0" onSubmit={handleSubmit}>
                      {error && (
                        <div className="mt-2.5 rounded-[var(--radius-main)] bg-[#f8e4e4] p-2.5 text-center text-sm text-[#3b0b0b]" role="alert">
                          {error}
                        </div>
                      )}

                      <label htmlFor="forget_email_input" className="mb-1 block text-sm font-medium text-neutral-darker">
                        <strong className="font-semibold">Email*</strong>
                      </label>
                      <input
                        ref={emailRef}
                        className="mb-3 w-full min-h-12 appearance-none rounded-[0.38rem] border border-neutral-lighter bg-transparent px-4 py-2 pl-10 font-sans text-base leading-none text-[#333333] placeholder:text-[#868c98] placeholder:text-sm focus:border-[color-mix(in_srgb,var(--color-pink-dark)_30%,var(--color-neutral-lighter))] focus:outline-none"
                        maxLength={256}
                        name="email"
                        placeholder="joe@domain.com"
                        type="email"
                        id="forget_email_input"
                        autoComplete="email"
                        required
                        style={{ backgroundImage: 'url("https://cdn.prod.website-files.com/686229256a807206c1874545/687a1dda10755faf27b69888_mail-line.svg")', backgroundPosition: '12px', backgroundRepeat: 'no-repeat', backgroundSize: 'auto' }}
                      />

                      <div className="w-full pt-4" />
                      <button
                        type="submit"
                        className="inline-flex w-full max-w-none cursor-pointer items-center justify-center gap-2 rounded-[var(--radius-large)] border-0 bg-[#762a5c] px-8 py-4 text-center text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-none text-white whitespace-nowrap transition-transform duration-200 ease-[cubic-bezier(0.39,0.575,0.565,1)] hover:-translate-y-[5%] active:scale-95"
                        disabled={submitting}
                      >
                        {submitting ? "Please wait..." : "Send reset link on email"}
                      </button>
                      <div className="w-full pt-4" />

                      <div className="text-center text-sm text-neutral">
                        Remember your password?{" "}
                        <Link to="/app/auth/login" className="text-[#762a5c] underline">
                          Login
                        </Link>
                      </div>
                    </form>
                  )}
                </div>
              </div>

              <div className="mb-6 text-sm text-neutral-dark">
                <div>&copy; {new Date().getFullYear()} BollyRhythm</div>
              </div>
            </div>

            <div
              className="relative m-2 flex items-center justify-center overflow-clip rounded-2xl bg-pink-darker text-center text-white max-[991px]:order-[-1] max-[991px]:bg-[length:auto] max-[991px]:bg-[0_0] max-[991px]:bg-repeat max-[991px]:!bg-none"
              style={{ backgroundImage: 'url("https://cdn.prod.website-files.com/686229256a807206c1874545/687a15da50e97523b48616d2_Auth%20Image.avif")', backgroundPosition: '50%', backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }}
            >
              <div className="absolute inset-[auto_auto_0%_0%] z-[1] flex text-white max-[991px]:ml-[-8vw]" style={{ height: 'clamp(12rem, 8.2857rem + 18.5714vw, 25rem)' }} />
              <div className="absolute inset-[auto_0%_0%_auto] z-[1] flex text-white max-[991px]:mr-[-8vw]" style={{ height: 'clamp(12rem, 8.2857rem + 18.5714vw, 25rem)' }} />
              <div className="mt-10 flex flex-col items-center justify-center max-[767px]:mt-20 max-[767px]:mb-6">
                <div className="flex flex-none items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-white)_25%,transparent)] text-white" style={{ width: 'clamp(2.5rem, 2.2143rem + 1.4286vw, 3.5rem)', minWidth: 'clamp(2.5rem, 2.2143rem + 1.4286vw, 3.5rem)', minHeight: 'clamp(2.5rem, 2.2143rem + 1.4286vw, 3.5rem)', aspectRatio: '1' }}>
                  <div className="flex" style={{ width: 'clamp(1.75rem, 1.6786rem + 0.3571vw, 2rem)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 32 33" fill="none">
                      <path
                        d="M30 13.512C30 22.262 17.0262 29.3445 16.4737 29.637C16.3281 29.7153 16.1654 29.7563 16 29.7563C15.8346 29.7563 15.6719 29.7153 15.5262 29.637C14.9738 29.3445 2 22.262 2 13.512C2.00232 11.4572 2.81958 9.48735 4.27248 8.03444C5.72539 6.58154 7.69528 5.76428 9.75 5.76196C12.3313 5.76196 14.5912 6.87196 16 8.74821C17.4088 6.87196 19.6688 5.76196 22.25 5.76196C24.3047 5.76428 26.2746 6.58154 27.7275 8.03444C29.1804 9.48735 29.9977 11.4572 30 13.512Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </div>
                <div className="w-full pt-4" />
                <div className="font-display text-[clamp(1.25rem,1.0357rem+1.0714vw,2rem)] font-semibold leading-[1.2]">
                  Our Moves.
                  <br />
                  <span className="text-[#eba4d3]">Your Dance.</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
